import { useState, type FormEvent } from 'react';
import { useFlags, useSetFlag } from '../api/hooks';
import { IMAGES_KILL_SWITCH, type FeatureFlag } from '../api/types';
import { PageHeader } from '../app/Shell';
import { Badge, Button, Card, Checkbox, ConfirmDialog, DataTable, ErrorNote, Field, QueryView, REASON_MAX, ReasonField, fmtAgo, fmtDateTime, reasonError, toast } from '../ui';
import s from './Flags.module.css';

const KEY_RE = /^[a-z0-9][a-z0-9_.-]{1,63}$/;

interface PendingChange {
  key: string;
  enabled: boolean;
  /** Kill-switch changes always need a note; ordinary flags accept an empty one. */
  requireNote: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  danger: boolean;
}

export function FlagsPage() {
  const flags = useFlags();
  const [pending, setPending] = useState<PendingChange | null>(null);

  return (
    <>
      <PageHeader title="功能開關" description="API 及 worker 即時讀取的開關。只限擁有者修改，並記錄在審計日誌。" />
      <QueryView query={flags}>
        {(list) => {
          const kill = list.find((f) => f._id === IMAGES_KILL_SWITCH);
          const others = list.filter((f) => f._id !== IMAGES_KILL_SWITCH);
          return (
            <>
              <KillSwitch flag={kill} onChange={setPending} />
              <Card title="其他開關" sub={`${others.length}`} flush>
                <DataTable
                  rows={others}
                  rowKey={(f) => f._id}
                  empty="還沒有其他開關。"
                  initialSort={{ key: 'key', dir: 'asc' }}
                  columns={[
                    { key: 'key', header: 'Key', render: (f) => <code>{f._id}</code>, sort: (f) => f._id },
                    { key: 'enabled', header: '狀態', render: (f) => (f.enabled ? <Badge tone="ok">開</Badge> : <Badge>關</Badge>), sort: (f) => (f.enabled ? 1 : 0) },
                    { key: 'rollout', header: '覆蓋', align: 'right', render: (f) => `${f.rolloutPercent ?? 100}%` },
                    { key: 'note', header: '備註', render: (f) => f.note || <span className="faint">—</span> },
                    { key: 'updated', header: '更新', render: (f) => <span title={fmtDateTime(f.updatedAt)}>{fmtAgo(f.updatedAt)}</span>, sort: (f) => f.updatedAt ?? '' },
                    {
                      key: 'action',
                      header: '',
                      render: (f) => (
                        <Button size="sm" onClick={() => setPending(toggleChange(f))}>
                          {f.enabled ? '關閉…' : '開啟…'}
                        </Button>
                      ),
                    },
                  ]}
                />
              </Card>
              <NewFlag existing={list} onSubmit={setPending} />
            </>
          );
        }}
      </QueryView>
      {pending && <FlagChangeDialog change={pending} onClose={() => setPending(null)} />}
    </>
  );
}

const toggleChange = (f: FeatureFlag): PendingChange => ({
  key: f._id,
  enabled: !f.enabled,
  requireNote: false,
  title: `${f.enabled ? '關閉' : '開啟'} ${f._id}？`,
  description: `每個伺服器程序下次讀取時，這個開關會變成「${f.enabled ? '關' : '開'}」。`,
  confirmLabel: f.enabled ? '關閉開關' : '開啟開關',
  danger: false,
});

function KillSwitch({ flag, onChange }: { flag: FeatureFlag | undefined; onChange: (c: PendingChange) => void }) {
  const stopped = flag?.enabled === true;
  return (
    <section className={s.kill} data-stopped={stopped} aria-labelledby="kill-title">
      <div className={s.killText}>
        <div className="row">
          <h2 id="kill-title">緊急開關：停止新的繪製版 AI 圖像</h2>
          {stopped ? <Badge tone="danger">圖像已停止</Badge> : <Badge tone="ok">圖像運作中</Badge>}
        </div>
        <p className="muted">
          開關 <code>{IMAGES_KILL_SWITCH}</code>。開啟後，繪製版漫畫不再呼叫圖像 AI，改用已審批的素材合成頁面（標記為 fallback）。每個 worker 最多每 30 秒重新讀取一次。
        </p>
        {flag && (
          <p className="small faint">
            上次改動 {fmtAgo(flag.updatedAt)}
            {flag.note ? ` · “${flag.note}”` : ''}
          </p>
        )}
      </div>
      <Button
        variant={stopped ? 'primary' : 'danger'}
        onClick={() =>
          onChange(
            stopped
              ? {
                  key: IMAGES_KILL_SWITCH,
                  enabled: false,
                  requireNote: true,
                  title: '恢復繪製版 AI 圖像？',
                  description: '新的漫畫 job 會再次使用付費圖像 AI。請先確認供應商事故或花費問題已解決。',
                  confirmLabel: '恢復圖像',
                  danger: false,
                }
              : {
                  key: IMAGES_KILL_SWITCH,
                  enabled: true,
                  requireNote: true,
                  title: '停止新的繪製版 AI 圖像？',
                  description: '在重新開啟之前，繪製版頁面會改用已審批的素材合成。大約 30 秒內生效。',
                  confirmLabel: '停止圖像',
                  danger: true,
                },
          )
        }
      >
        {stopped ? '恢復圖像…' : '停止圖像…'}
      </Button>
    </section>
  );
}

function NewFlag({ existing, onSubmit }: { existing: FeatureFlag[]; onSubmit: (c: PendingChange) => void }) {
  const [key, setKey] = useState('');
  const [enabled, setEnabled] = useState(true);
  const k = key.trim();
  const exists = existing.some((f) => f._id === k);
  const error = k === '' ? null : !KEY_RE.test(k) ? '只可用小寫字母、數字、「_」「.」「-」，2 至 64 個字元。' : exists ? '已經存在，請在表格中切換。' : null;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!k || error) return;
    onSubmit({
      key: k,
      enabled,
      requireNote: false,
      title: `建立開關 ${k}？`,
      description: `以「${enabled ? '開' : '關'}」狀態建立。程式要讀取這個 key 才會生效。`,
      confirmLabel: '建立開關',
      danger: false,
    });
  };

  return (
    <Card title="新增開關">
      <form className="row" style={{ alignItems: 'flex-end', gap: 'var(--sp-4)' }} onSubmit={submit}>
        <div style={{ width: 280 }}>
          <Field label="Key" error={error}>
            {(id) => <input id={id} type="text" value={key} onChange={(e) => setKey(e.target.value)} placeholder="例如 rooms_enabled" className="mono" />}
          </Field>
        </div>
        <div style={{ paddingBottom: 6 }}>
          <Checkbox checked={enabled} onChange={setEnabled}>
            開啟
          </Checkbox>
        </div>
        <Button type="submit" disabled={!k || !!error}>
          新增…
        </Button>
      </form>
    </Card>
  );
}

function FlagChangeDialog({ change, onClose }: { change: PendingChange; onClose: () => void }) {
  const [note, setNote] = useState('');
  const setFlag = useSetFlag();
  const noteOk = change.requireNote ? !reasonError(note) : note.length <= REASON_MAX;
  return (
    <ConfirmDialog
      open
      title={change.title}
      confirmLabel={change.confirmLabel}
      tone={change.danger ? 'danger' : 'primary'}
      busy={setFlag.isPending}
      canConfirm={noteOk}
      onCancel={onClose}
      onConfirm={() =>
        setFlag.mutate(
          { key: change.key, enabled: change.enabled, note: note.trim() },
          {
            onSuccess: () => {
              toast(`${change.key} 已${change.enabled ? '開啟' : '關閉'}`);
              onClose();
            },
          },
        )
      }
      error={setFlag.isError ? <ErrorNote error={setFlag.error} /> : null}
    >
      <p className="muted">{change.description}</p>
      <ReasonField
        label={change.requireNote ? '備註（必填）' : '備註'}
        hint={change.requireNote ? '原因，例如事故連結或花費警報。' : '選填；會顯示在開關旁及審計日誌。'}
        value={note}
        onChange={setNote}
        touched={change.requireNote}
        autoFocus
      />
    </ConfirmDialog>
  );
}
